<template>
  <div v-if="isVisible && (unlockedRewardsAreVisible || genericOffersAreVisible)">
    <template v-if="unlockedRewardsAreVisible">
      <div class="layout layout--horizontal">
        <h3 class="title title--6 half-spacing">
          Unlocked Rewards
        </h3>

        <button
          v-if="shouldShowAllUnlockedRewards || hiddenUnlockedThresholdsCount > 0"
          type="button"
          class="link link--icon-at-end primary half-spacing smaller"
          @click="toggleShowAllUnlockedRewards"
        >
          <template v-if="shouldShowAllUnlockedRewards">
            <span>Show Less</span>
            <span class="icon far fa-chevron-down" />
          </template>
          <template v-else>
            <span>{{ hiddenUnlockedThresholdsCount }} more unlocked reward<template v-if="hiddenUnlockedThresholdsCount > 1">s</template></span>
            <span class="icon far fa-chevron-right" />
          </template>
        </button>
      </div>

      <div class="card-list card-list--clickable card-list--smaller-mobile-titles">
        <transition-group
          tag="ul"
          name="list-slide"
          class="relative"
        >
          <li
            v-for="threshold in visibleUnlockedThresholds"
            :key="threshold.publicId"
          >
            <div
              class="card-list__item"
              @click="viewOffer(threshold.rewardOffer)"
            >
              <div
                v-if="threshold.rewardOffer.couponImageUrl"
                class="card-list__thumbnail"
                :style="`background-image: url(${ threshold.rewardOffer.couponImageUrl });`"
              />

              <div class="card-list__item-section grow">
                <p class="title title--5 dark no-wrap-ellipsis-multi-line">{{ threshold.rewardOffer.title }}</p>
              </div>

              <div class="card-list__item-section no-shrink">
                <p class="tag">{{ threshold.cost }} {{ loyalty.currency.pluralShortNameUpperCase }}</p>
              </div>

              <div class="card-list__item-section no-shrink">
                <button
                  type="button"
                  class="button button--small button--secondary normalcase"
                  :class="{
                    'button--loading': selectedOfferId === threshold.rewardOffer.loyaltyConversionThresholdPublicId
                  }"
                  :disabled="disableRedeemButton"
                  @click.stop="apply(threshold.rewardOffer)"
                >
                  {{ content.Loyalty.metadata.buttonCta }}
                </button>
              </div>
            </div>
          </li>
        </transition-group>
      </div>
    </template>



    <template v-if="genericOffersAreVisible">
      <h3 class="title title--6 half-spacing">
        {{ content.OrderSummary.metadata.rewards }}
      </h3>

      <div class="card-list card-list--clickable">
        <div
          v-for="offer in offers.allRedeemableOffers"
          :key="offer.offerAllocationPublicId"
          class="card-list__item"
          @click="viewOffer(offer)"
        >
          <div
            v-if="offer.couponImageUrl"
            class="card-list__thumbnail"
            :style="`background-image: url(${offer.couponImageUrl});`"
          />

          <div class="card-list__item-section card-list__item-section--block grow">
            <p class="title title--5 dark no-wrap-ellipsis">{{ offer.title }}</p>
            <p
              v-if="offer.endDate"
              class="smaller dark-gray"
            >
              Expires {{ $moment(offer.endDate, 'M/D/YY') }}
            </p>
          </div>

          <div class="card-list__item-section no-shrink">
            <button
              type="button"
              class="button button--small button--primary normalcase"
              :class="{
                'button--loading': selectedOfferId === offer.offerAllocationPublicId
              }"
              :disabled="disableRedeemButton"
              @click.stop="apply(offer)"
            >
              {{ content.Loyalty.metadata.buttonCta }}
            </button>
          </div>
        </div>
      </div>
    </template>

    <verify-account-cta v-if="appliedOffers.length === 0" />

    <modal
      v-slot="{ data }"
      name="offerDetails"
      modal-style="compact"
    >
      <image-loader
        v-if="data.offer.couponDetailsImageUrl"
        class="no-spacing"
        :alt-text="data.offer.title"
        :source="data.offer.couponDetailsImageUrl"
      />

      <div class="section section--half-padding">
        <div class="flex gap justify-between align-center half-spacing">
          <h3 class="title title--3 bold grow no-spacing">
            {{ data.offer.title }}
          </h3>

          <span
            v-if="data.offer.cost"
            class="tag tag--bigger no-shrink"
          >
            {{ data.offer.cost }} {{ loyalty.currency.pluralShortNameUpperCase }}
          </span>
          <span
            v-else
            class="icon icon--bigger far fa-gift primary"
          />
        </div>
        <p class="bigger content">{{ data.offer.description }}</p>
      </div>

      <div
        v-if="data.offer.finePrint"
        class="section section--half-padding"
      >
        <h4 class="title title--6 half-spacing">Terms & Conditions</h4>
        <formatted-content
          class="smaller"
          :content="data.offer.finePrint"
        />
      </div>

      <div class="section section--alt-background section--half-padding">
        <button
          :class="['button button--primary button--large normalcase', {
            'button--loading': [data.offer.loyaltyConversionThresholdPublicId, data.offer.offerAllocationPublicId].includes(selectedOfferId)
          }]"
          type="button"
          :disabled="disableRedeemButton"
          @click="apply(data.offer)"
        >
          {{ content.Loyalty.metadata.buttonCta }}
        </button>

        <p
          v-if="data.offer.endDate"
          class="center"
        >
          Expires {{ $moment(data.offer.endDate, 'M/D/YY') }}
        </p>

        <verify-account-cta />
      </div>
    </modal>
  </div>
</template>



<script>
  import events from '@/services/events';
  import errorHandlers from '@/services/error-handlers';
  import { mapGetters } from 'vuex';



  export default {
    name: 'ApplyCardfreeOffer',

    data() {
      return {
        selectedOfferId: null,
        shouldShowAllUnlockedRewards: false
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('offers', ['loyalty', 'offers']),
      ...mapGetters('orders', ['order']),
      ...mapGetters('session', ['currentUser']),

      isVisible() {
        return !!this.currentUser.accountId && this.order.orderAttributes.canAddAccountOffer;
      },

      appliedOffers() {
        return this.order.offers.filter(o => o.applicationStatus === 'Applied');
      },

      disableRedeemButton() {
        return !!this.selectedOfferId || !this.currentUser.allowOfferRedemption || this.appliedOffers.length > 0;
      },

      genericOffersAreVisible() {
        return this.offers.allRedeemableOffers.length > 0;
      },

      hiddenUnlockedThresholdsCount() {
        return this.loyalty.unlockedThresholds.length - this.visibleUnlockedThresholds.length;
      },

      unlockedRewardsAreVisible() {
        return this.loyalty.isMultiTiered && this.loyalty.unlockedThresholds.length > 0;
      },

      visibleUnlockedThresholds() {
        if (this.shouldShowAllUnlockedRewards) {
          return this.loyalty.unlockedThresholds;
        }

        if (this.loyalty.appliedThresholds.length > 0) {
          return this.loyalty.appliedThresholds;
        }

        return this.loyalty.bestUnlockedThreshold ? [this.loyalty.bestUnlockedThreshold] : [];
      }
    },

    created() {
      this.createdHandler();
    },

    methods: {
      async apply(offer) {
        try {
          this.selectedOfferId = offer.loyaltyConversionThresholdPublicId || offer.offerAllocationPublicId;

          await this.$store.dispatch('orders/redeemOffer', {
            loyaltyConversionThresholdPublicId: offer.loyaltyConversionThresholdPublicId,
            offerAllocationPublicId: offer.offerAllocationPublicId
          });

          this.$closeModal('offerDetails');

          events.emit('notification', {
            type: 'success',
            message: 'Your offer was applied.'
          });

          const eventData = { offer };

          this.$googleEventTracking.postEvent('select_promotion', eventData);

          // this.$triggerGoogleTrackingEvent('ApplyOffer', 'Loyalty');

          this.$triggerNewRelicEvent('ApplyOffer', 'Information', 'Applied Offer');
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.selectedOfferId = null;
        }
      },

      createdHandler() {
        this.loadOffers();
      },

      loadOffers() {
        this.$store.dispatch('offers/getOffers', {
          silent: true
        });
      },

      toggleShowAllUnlockedRewards() {
        this.shouldShowAllUnlockedRewards = !this.shouldShowAllUnlockedRewards;
      },

      viewOffer(offer) {
        this.$openModal('offerDetails', {
          offer
        });

        const eventData = { offer };

        this.$googleEventTracking.postEvent('view_promotion', eventData);
      }
    }
  };
</script>
